import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './CommentsChatFooter.module.scss';
import { Button, Icon, Input, notification, message, Modal, Spin } from 'antd';
import CommentOptions from '../../CommentOptions/CommentOptions';
import { useDispatch, useSelector } from 'react-redux';
import AttachedImages from '../attachedFiles/AttachedImages/AttachedImages';
import AttachedCommonFiles from '../attachedFiles/AttachedCommonFiles/AttachedCommonFiles';
import {
  setCurrentComment,
  setIsBeingEditedMessageAction
} from '../../../../actions/commentsActions/commentsActions';
import { FILE_SIZE_LIMIT } from './constants';
import { maxCommentSize } from '../../../../actions/commentsActions/constants';

const { TextArea } = Input;
const { confirm } = Modal;

const CommentsChatFooter = ({
  entityId,
  showCheckboxOptions = false,
  sendComment,
}) => {
  const dispatch = useDispatch();
  const attachFilesInput = useRef();
  const isBeingEdited = useSelector(
    (state) => state.commentsChatStore.isCommentBeingEdited
  );
  const editedComment = useSelector(
    (state) => state.commentsChatStore.editedComment
  );
  const currentComment = useSelector(
    (state) => state.commentsChatStore.currentComment
  );
  const isLoading = useSelector(
    (state) => state.commentsChatStore.comments.isLoading
  );
  const [attachedFiles, setAttachedFiles] = useState({
    images: [],
    commonFiles: [],
  });
  const [commentValue, setCommentValue] = useState('');

  useEffect(() => {
    if (isBeingEdited) {
      setCommentValue(editedComment.message);
      setAttachedFiles({
        images: editedComment.attachments.images
          ? editedComment.attachments.images
          : [],
        commonFiles: editedComment.attachments.files
          ? editedComment.attachments.files
          : [],
      });
    } else {
      setCommentValue('');
      setAttachedFiles({
        images: [],
        commonFiles: [],
      });
    }
  }, [isBeingEdited, editedComment, entityId]);

  useEffect(() => {
    dispatch(
      setCurrentComment({ message: '', attachments: { images: [], files: [] } })
    );
  }, [entityId]);

  useEffect(() => {
    dispatch(
      setCurrentComment({
        message: commentValue,
        attachments: {
          files: attachedFiles.commonFiles,
          images: attachedFiles.images,
        },
      })
    );
  }, [commentValue, attachedFiles]);

  const onTextAreaChange = useCallback(
    (e) => {
      if (isBeingEdited && e.target.value.length > maxCommentSize) {
        message.error(
          'Редактируемое сообщение не может превысить допустимую длину.'
        );
      } else {
        setCommentValue(e.target.value);
      }
    },
    [isBeingEdited, entityId]
  );

  const isSendDisabled = useMemo(() => {
    return !(
      currentComment.message ||
      currentComment.attachments.images.length ||
      currentComment.attachments.files.length
    );
  }, [currentComment]);

  const clickSend = useCallback(() => {
    console.log(
      `"${commentValue}"`,
      attachedFiles.commonFiles.length,
      attachedFiles.images.length
    );
    if (
      commentValue ||
      attachedFiles.commonFiles.length ||
      attachedFiles.images.length
    ) {
      if (isBeingEdited) {
        sendComment(editedComment.id, commentValue, attachedFiles, true);
      } else {
        sendComment(entityId, commentValue, attachedFiles, false);
      }

      setCommentValue('');
      setAttachedFiles({ images: [], commonFiles: [] });
    }
  }, [entityId, sendComment, commentValue, attachedFiles]);

  const onFileChange = (event) => {
    const attachedFilesArray = event.target.files;
    const commonFilesArray = [];
    const imageFilesArray = [];
    for (let file of attachedFilesArray) {
      const addedFile = file;
      console.log(addedFile);

      if (addedFile.size > FILE_SIZE_LIMIT) {
        openErrorNotification(
          'Размер прикрепляемого файла не должен превышать 15 MB.'
        );
        attachFilesInput.current.value = null;
        return;
      }

      if (addedFile.type.includes('image')) {
        imageFilesArray.push(addedFile);
      } else {
        commonFilesArray.push(addedFile);
      }
      setAttachedFiles({
        commonFiles: [...attachedFiles.commonFiles, ...commonFilesArray],
        images: [...attachedFiles.images, ...imageFilesArray],
      });
    }
    attachFilesInput.current.value = null;
  };

  const deleteAttachedCommonFile = useCallback(
    (index) => {
      const newAttachedFiles = attachedFiles.commonFiles;
      newAttachedFiles.splice(index, 1);
      setAttachedFiles({
        ...attachedFiles,
        commonFiles: [...newAttachedFiles],
      });
    },
    [attachedFiles, entityId]
  );

  const deleteAttachedImage = useCallback(
    (index) => {
      const newAttachedImages = attachedFiles.images;
      newAttachedImages.splice(index, 1);
      setAttachedFiles({ ...attachedFiles, images: [...newAttachedImages] });
    },
    [attachedFiles, entityId]
  );

  const confirmCancelEdit = (e) => {
    e.preventDefault();
    confirm({
      title: 'Вы уверены, что хотите прекратить редактирование?',
      onOk() {
        dispatch(setIsBeingEditedMessageAction(false));
      },
      okText: 'Да',
      cancelText: 'Нет',
      onCancel() {},
    });
  };

  const openErrorNotification = (description) => {
    notification['error']({
      message: 'Ошибка',
      description,
    });
  };

  const clickAttach = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const messageKeyDown = useCallback(
    (e) => {
      if (!e.shiftKey && e.key === 'Enter') {
        e.preventDefault();
        clickSend();
      }
    },
    [clickSend]
  );

  return (
    <div className={styles.footer} onKeyDown={messageKeyDown}>
      {isBeingEdited ? (
        <div className={styles.editing}>
          <div>
            <div>
              Редактирование комментария
              <span>
                {isLoading ? (
                  <Spin />
                ) : (
                  `, ${commentValue?.length}/
              ${maxCommentSize} символов`
                )}
              </span>
            </div>
          </div>

          {!isLoading ? (
            <Button
              className={styles.cancelEditing}
              style={{ border: 'none' }}
              icon={'close'}
              size="large"
              onClick={confirmCancelEdit}
            />
          ) : null}
        </div>
      ) : null}
      {showCheckboxOptions ? <CommentOptions /> : null}

      <AttachedImages
        attachedImages={attachedFiles.images}
        deleteAttachedImage={deleteAttachedImage}
      />

      <div className={styles.inputSendBlock}>
        <div className={styles.inputMessageBlock}>
          <TextArea
            autoFocus
            value={commentValue}
            onChange={onTextAreaChange}
            className={styles.textArea}
            placeholder="Сообщение"
            autoSize={{ minRows: 1, maxRows: 4 }}
          />
          <label className={styles.attachButton} onClick={clickAttach}>
            <input
              multiple
              ref={attachFilesInput}
              className={styles.attachFilesInput}
              type="file"
              onChange={onFileChange}
            />
            <Icon type={'paper-clip'} />
          </label>
        </div>
        <Button
          disabled={isSendDisabled}
          type={'primary'}
          loading={isLoading}
          shape={'circle'}
          icon={'arrow-up'}
          style={isSendDisabled ? { background: '#7aabff' } : null}
          className={styles.sendButton}
          onClick={clickSend}
        />
      </div>
      <AttachedCommonFiles
        attachedFiles={attachedFiles.commonFiles}
        deleteAttachedCommonFile={deleteAttachedCommonFile}
      />
    </div>
  );
};

export default memo(CommentsChatFooter);
